// Phase RV-6a — FLEX builder hook.
//
// Reads the localStorage draft (RV-5 stub key: strikza_flex_draft_v1),
// hydrates each candidate_id against the upcoming board pool, and exposes
// add/remove/clear + lockSlip mutators.
//
// Realtime subscription uses useId() per-mount, matching the JB-arc /
// usePendingDrift / useUpcomingBoard pattern.
//
// N15: FLEX outcomes never affect chain progression — this hook never
// reads or writes any chain table (grep-enforced in nRules.test.ts).

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { BoardCandidate } from "./upcomingBoardHelpers.ts";
import {
  type FlexLeg,
  canLock,
  combinedOddsOf,
  toFlexLeg,
  validateContradictions,
  validateNoSameFixture,
} from "./flexBuilderHelpers.ts";

const FLEX_DRAFT_KEY = "strikza_flex_draft_v1";

export interface UseFlexBuilderOptions {
  pool: readonly BoardCandidate[];
  windowLabel: BoardCandidate["window_label"] | null;
  windowPublicationAt: Date | null;
  now?: Date;
}

export interface UseFlexBuilderState {
  legs: FlexLeg[];
  combinedOdds: number;
  sameFixtureConflict: ReturnType<typeof validateNoSameFixture>;
  contradictions: ReturnType<typeof validateContradictions>;
  lockability: ReturnType<typeof canLock>;
  addLeg: (candidate: BoardCandidate) => void;
  removeLeg: (candidateId: string) => void;
  clearDraft: () => void;
  lockSlip: (stake: number | null) => Promise<
    { ok: true; flex_slip_id: string; combined_odds: number; leg_count: number; potential_return: number | null }
    | { ok: false; reason: string }
  >;
  locking: boolean;
  lockError: string | null;
}

function readDraftIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(FLEX_DRAFT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string");
  } catch {
    return [];
  }
}

function writeDraftIds(ids: readonly string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FLEX_DRAFT_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage may be unavailable */
  }
}

export function useFlexBuilder(
  opts: UseFlexBuilderOptions,
): UseFlexBuilderState {
  const { pool, windowLabel, windowPublicationAt, now } = opts;
  const { user } = useAuthSession();
  const channelId = useId();
  const [draftIds, setDraftIds] = useState<string[]>(() => readDraftIds());
  const [locking, setLocking] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);

  // Realtime: invalidate locally when our own row changes (cross-tab sync).
  useEffect(() => {
    if (!user) return;
    const client = getBrowserClient();
    const channel: RealtimeChannel = client
      .channel(`flex-builder-${channelId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "rollover_flex_slips",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // No state to refetch here — the locked slip clears the draft.
        },
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [user, channelId]);

  const legs = useMemo<FlexLeg[]>(() => {
    const byId = new Map<string, BoardCandidate>(
      pool.map((c) => [c.candidate_id, c]),
    );
    const out: FlexLeg[] = [];
    for (const id of draftIds) {
      const cand = byId.get(id);
      if (cand) out.push(toFlexLeg(cand));
    }
    return out;
  }, [pool, draftIds]);

  const combinedOdds = useMemo(() => combinedOddsOf(legs), [legs]);
  const sameFixtureConflict = useMemo(() => validateNoSameFixture(legs), [legs]);
  const contradictions = useMemo(() => validateContradictions(legs), [legs]);
  const lockability = useMemo(() => canLock(legs, now), [legs, now]);

  const addLeg = useCallback((candidate: BoardCandidate) => {
    setDraftIds((prev) => {
      if (prev.includes(candidate.candidate_id)) return prev;
      const next = [...prev, candidate.candidate_id];
      writeDraftIds(next);
      return next;
    });
  }, []);

  const removeLeg = useCallback((candidateId: string) => {
    setDraftIds((prev) => {
      if (!prev.includes(candidateId)) return prev;
      const next = prev.filter((id) => id !== candidateId);
      writeDraftIds(next);
      return next;
    });
  }, []);

  const clearDraft = useCallback(() => {
    setDraftIds([]);
    writeDraftIds([]);
  }, []);

  const lockSlip = useCallback<UseFlexBuilderState["lockSlip"]>(
    async (stake) => {
      if (!user) return { ok: false, reason: "auth_required" };
      if (!windowLabel || !windowPublicationAt) {
        return { ok: false, reason: "window_unavailable" };
      }
      const check = canLock(legs, now);
      if (!check.ok) return { ok: false, reason: check.reason };
      setLocking(true);
      setLockError(null);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const client = getBrowserClient() as any;
        const payload = legs.map((l) => ({
          candidate_id: l.candidate_id,
          fixture_id: l.fixture_id,
          tier: l.tier,
          market: l.market,
          pick: l.pick,
          odds: l.odds,
          confidence_score: l.confidence_score,
        }));
        const { data, error } = await client.rpc("flex_lock_slip", {
          p_legs: payload,
          p_stake: stake,
          p_window_label: windowLabel,
          p_window_publication_at: windowPublicationAt.toISOString(),
        });
        if (error) {
          setLockError(error.message);
          return { ok: false, reason: error.message };
        }
        const result = data as { ok?: boolean; reason?: string } & Record<string, unknown>;
        if (result?.ok === true) {
          clearDraft();
          return {
            ok: true,
            flex_slip_id: String(result.flex_slip_id ?? ""),
            combined_odds: Number(result.combined_odds ?? 0),
            leg_count: Number(result.leg_count ?? 0),
            potential_return:
              result.potential_return === null || result.potential_return === undefined
                ? null
                : Number(result.potential_return),
          };
        }
        const reason = typeof result?.reason === "string" ? result.reason : "lock_failed";
        setLockError(reason);
        return { ok: false, reason };
      } finally {
        setLocking(false);
      }
    },
    [user, windowLabel, windowPublicationAt, legs, now, clearDraft],
  );

  return {
    legs,
    combinedOdds,
    sameFixtureConflict,
    contradictions,
    lockability,
    addLeg,
    removeLeg,
    clearDraft,
    lockSlip,
    locking,
    lockError,
  };
}
